import { ClockIcon, PencilSquareIcon } from '@heroicons/react/16/solid';
import { isToday } from 'date-fns';
import { Fragment, useCallback, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import { usePageContext } from 'vike-react/usePageContext';

import Skeleton from '@/components/UI/Skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/UI/table';
import { menuHref } from '@/components/react-aria/menu';
import {
  Pagination,
  PaginationGap,
  PaginationList,
  PaginationNext,
  PaginationPage,
  PaginationPrevious,
} from '@/components/react-aria/pgagination';
import { request } from '@/renderer/utils/api';
import { useNextInfiniteRequest } from '@/renderer/utils/api/infiniteStore';
import type { UseInfiniteRequestResult } from '@/renderer/utils/api/types';
import formatReleaseLine from '@/renderer/utils/formatReleaseLine';
import { useLocale } from '@/renderer/utils/locale';
import useMe from '@/renderer/utils/useMe';

import { useReconcileResults, useRegisterReconcile } from './ResultsReconcileContext';
import UpdateReleaseForm from './UpdateReleaseForm';

const formatScore = (
  visitorTeam: string | undefined,
  homeTeam: string | undefined,
  visitorScore: number,
  homeScore: number,
  overtimes: number,
) => {
  let score = '';

  if (visitorScore > homeScore) {
    score = `${visitorTeam} ${visitorScore}-${homeScore}`;
  } else if (homeScore > visitorScore) {
    score = `${homeTeam} ${homeScore}-${visitorScore}`;
  } else if (homeScore === visitorScore && homeScore !== null) {
    score = `TIE ${homeScore}-${visitorScore}`;
  }

  if (overtimes > 0) {
    score += `${score ? ' ' : ''}${overtimes > 1 ? overtimes : ''}OT`;
  }

  return score;
};

const formatValue = (
  value: number | null | undefined,
  nullish: string | JSX.Element = '',
  zero: string | JSX.Element | undefined = undefined,
) => {
  if (value === null || value === undefined) {
    return nullish;
  } else if (value > 0) {
    return `+${Number(value)}`;
  } else if (value === 0 && zero !== undefined) {
    return zero;
  }

  return `${Number(value)}`;
};

type ReleaseResult = 'pending' | 'win' | 'loss' | 'push' | 'canceled' | 'noAction';

export default function ResultsTable<
  const UID extends API.Id | 'me' | 'archive',
  const _ResponseType extends UID extends 'archive' ? API.Release : API.MessageReceipt,
>({
  sportId,
  serviceIds,
  playType,
  gameType, // Add this parameter
  startDate,
  endDate,
  userId,
  free,
  result,
}: {
  sportId?: number | number[];
  serviceIds?: number[];
  playType?: API.PlayTypes | API.PlayTypes[] | 'full' | 'st' | 'hp' | 'pf';
  gameType?: API.GameTypes[]; // Add this type
  startDate?: Date;
  endDate?: Date;
  userId: UID;
  free?: boolean;
  // Optional result filter for the member receipt list (e.g. exclude pending —
  // those live in the pending section — by requesting only graded results).
  result?: ReleaseResult | ReleaseResult[];
}) {
  const {
    urlParsed: { search },
  } = usePageContext();

  const limit = ALLOWED_LIMITS.includes(+search?.limit) ? +search?.limit : DEFAULT_LIMIT;
  const page = +search?.page || 1;

  const eventValidate = useCallback(
    async (
      _event: MessageEvent,
      ojson: API.SubscriptionResponse<API.MessageReceipt> | API.WebsocketResult<API.Id>,
    ) => {
      const data = ojson.data as API.MessageReceipt;

      if (!data.releaseId) {
        return false;
      }

      if (ojson.message === 'messageReceipt/updated') {
        return {
          ...ojson,
          data: (
            await request<API.WithData<API.MessageReceipt>>(
              `/api/v1/messageReceipt/${userId}/${ojson.data as number}`,
            )
          ).data,
        } satisfies API.SubscriptionResponse<API.MessageReceipt>;
      }
    },
    [userId],
  );

  const releasesRequest = useNextInfiniteRequest<API.Release, void, API.WithPageInput<API.Release>>(
    useMemo(
      () => ({
        key: 'fetchReleases',
        req: `/api/v1/release`,
        query: {
          order: {
            by: 'gameTime',
          },
          page: {
            limit,
            offset: limit * (page - 1),
          },
          data: {
            [Array.isArray(sportId) ? 'sportId$in' : 'sportId']: sportId,
            'message.serviceIds': serviceIds,
            playType:
              playType === 'full'
                ? ['side', 'total', 'prop', 'future']
                : playType === 'hp'
                  ? ['side', 'total']
                  : playType === 'st'
                    ? ['side', 'total']
                    : playType === 'pf'
                      ? ['prop', 'future']
                      : playType && typeof playType === 'string'
                        ? [playType]
                        : playType || ['side', 'total', 'prop', 'future'],
            gameType, // Add gameType here
            startDate,
            endDate,
            free,
          },
        },
        skipPoll: true,
        pause: userId !== 'archive',
      }),
      [limit, page, userId, sportId, serviceIds, playType, gameType, startDate, endDate, free], // Add gameType to dependencies
    ),
  );

  const messageReceiptsRequest = useNextInfiniteRequest<
    API.MessageReceipt,
    void,
    API.WithPageInput<API.ExtractNestedSortable<API.MessageReceipt>>
  >(
    useMemo(
      () => ({
        key: 'fetchMessageReleases',
        req: `/api/v1/messageReceipt/${userId}`,
        event: 'messageReceipt',
        eventValidate,
        query: {
          page: {
            limit,
            offset: limit * (page - 1),
          },
          data: {
            type: 'release',
            [Array.isArray(sportId) ? 'sportId$in' : 'sportId']: sportId,
            'message.serviceIds': serviceIds,
            result,
            playType:
              playType === 'full'
                ? ['side', 'total', 'prop', 'future']
                : playType === 'hp'
                  ? ['side', 'total']
                  : playType === 'st'
                    ? ['side', 'total']
                    : playType === 'pf'
                      ? ['prop', 'future']
                      : playType && typeof playType === 'string'
                        ? [playType]
                        : playType || ['side', 'total', 'prop', 'future'],
            gameType, // Add gameType here too
            startDate,
            endDate,
            free,
          },
        },
        skipPoll: true,
        pause: userId === 'archive',
      }),
      [
        limit,
        page,
        userId,
        eventValidate,
        sportId,
        serviceIds,
        playType,
        gameType,
        startDate,
        endDate,
        free,
        result,
      ], // Add gameType to dependencies
    ),
  );

  return (
    <ResultsTableContent
      userId={userId}
      infiniteRequest={userId === 'archive' ? releasesRequest : messageReceiptsRequest}
    />
  );
}

const ALLOWED_LIMITS = [1, 5, 10, 25, 50, 100];
const DEFAULT_LIMIT = 50;

export function ResultsTableContent<
  const UID extends API.Id | 'me' | 'archive',
  const _ResponseType extends UID extends 'archive' ? API.Release : API.MessageReceipt,
>({
  userId,
  infiniteRequest,
  hidePagination,
}: {
  userId: UID;
  infiniteRequest:
    | UseInfiniteRequestResult<API.Release, void>
    | UseInfiniteRequestResult<API.MessageReceipt, void>
    | null;
  // The pending section shows a small fixed list — no pagination of its own.
  hidePagination?: boolean;
}) {
  const intl = useLocale();
  let lastDate: string;

  const user = useMe();
  const isAdmin = user?.role === 'admin';
  const colSpan = isAdmin ? 7 : 6;

  // Register this list's refetch and grab the reconcile-all so a grade here (or
  // in the sibling pending list) refreshes BOTH lists live (#1284).
  useRegisterReconcile(infiniteRequest?.refetch);
  const reconcileResults = useReconcileResults();

  // Explicit per-column widths shared by every ResultsTableContent instance so the
  // pending table and the graded table below it (both rendered by this component)
  // resolve to IDENTICAL column boundaries and read as one continuous list (#1429).
  // `width: undefined` lets Play absorb the remaining space under `table-fixed`.
  const columns = useMemo(
    () =>
      [
        { name: 'Sport', showOn: 'medium', width: '5rem' },
        { name: 'Play', className: 'w-full', width: undefined },
        { name: 'Result', width: '8rem' },
        { name: 'Score', showOn: 'large', width: '10rem' },
        { name: 'Closing line', showOn: 'large', width: '8rem' },
        { name: 'CLV', width: '5rem' },
        ...(isAdmin
          ? [
              {
                name: 'Edit',
                width: '3.5rem',
              },
            ]
          : []),
      ] as const,
    [isAdmin],
  );

  const {
    isPending,
    error,
    data: results,
    isLoading,
  } = infiniteRequest || {
    isPending: true,
    error: null,
    data: null,
  };

  const rows = results?.data || [];
  const hasRows = rows?.length > 0;

  const {
    urlParsed: { pathname, search },
  } = usePageContext();

  const limit = ALLOWED_LIMITS.includes(+search?.limit) ? +search?.limit : DEFAULT_LIMIT;
  const page = +search?.page || 1;

  const perPage = results?.pageInfo.limit || limit || results?.pageInfo.total || 1;
  const pageCount = Math.ceil((results?.pageInfo.total || 0) / perPage) || 1;

  return (
    <>
      {/* Mobile (below md): stacked play-cards. The desktop table crammed a matchup
          into ~80px and wrapped it to 4 lines with the FCS badge splitting to "FC/S"
          (#1542/#1574). Cards give the title full width and keep badges intact. */}
      <ResultsCardList
        rows={rows}
        userId={userId}
        isAdmin={isAdmin}
        isPending={isPending}
        hasRows={hasRows}
        onGrade={reconcileResults ?? infiniteRequest?.refetch}
      />
      {/* Desktop (md+): the original results table, unchanged. */}
      <div className="hidden md:block">
        {/* Tighter horizontal cell padding on phones so the 3 mobile columns
          (Play / Result / CLV) fit without the table scrolling sideways. */}
        <Table className="mt-12 table-fixed max-sm:[&_td]:px-2 max-sm:[&_th]:px-2">
          {/* Fixed, shared column widths so this table lines up column-for-column
            with the sibling pending table above it (#1429). Cols mirror the
            responsive show/hide of their cells so hidden columns reserve no
            width on narrow viewports. */}
          <colgroup>
            {columns.map((column) => (
              <col
                key={column.name}
                className={twMerge(
                  column.showOn === 'medium' && 'hidden md:table-column',
                  column.showOn === 'large' && 'hidden lg:table-column',
                  // Carry the column's own width driver (e.g. Play's `w-full`)
                  // onto the always-present <col>, not just the md-hidden header,
                  // so table-fixed lets Play absorb the leftover width on mobile
                  // instead of collapsing to ~1ch and wrapping per-character
                  // (regression from #1432's table-auto -> table-fixed switch).
                  column.className,
                )}
                style={column.width ? { width: column.width } : undefined}
              />
            ))}
          </colgroup>
          <TableHeader className="hidden md:contents">
            <TableRow>
              {columns.map((column) => (
                <TableHead
                  key={column.name}
                  className={twMerge(
                    'sticky top-0 bg-white px-4 py-2',
                    column.showOn === 'medium' && 'hidden md:table-cell',
                    column.showOn === 'large' && 'hidden lg:table-cell',
                    column.className,
                  )}
                  breakpoint={
                    column.showOn === 'medium' ? 'md' : column.showOn === 'large' ? 'lg' : undefined
                  }
                >
                  {column.name == 'Edit'
                    ? isAdmin && <span className="sr-only">Edit</span>
                    : column.name}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {!hasRows &&
              isPending &&
              Array(colSpan)
                .fill(1)
                .map((_, index) => (
                  <Fragment key={index}>
                    <TableRow className="border-zinc-900/10 border-b dark:border-white/20">
                      <TableHead
                        colSpan={colSpan}
                        scope="colgroup"
                        className="bg-zinc-50 py-2 pr-3 pl-4 text-left font-normal text-sm text-zinc-900 sm:pl-3 dark:bg-white/5 dark:text-white"
                      >
                        <Skeleton className="inline-block" loading={true}>
                          Safudsday, Jebrugber 32, 2099
                        </Skeleton>
                      </TableHead>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Skeleton className="inline-block" loading={true}>
                          CCC
                        </Skeleton>
                      </TableCell>
                      <TableCell>
                        <Skeleton className="inline-block" loading={true}>
                          000 Sports Team +100 at Sportier Team
                        </Skeleton>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div className="inline-flex items-center space-x-2">
                          <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-zinc-500/50" />
                          <Skeleton className="inline-block" loading={true}>
                            Pending
                          </Skeleton>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Skeleton className="inline-block" loading={true}>
                          000 - 000
                        </Skeleton>
                      </TableCell>
                      <TableCell>
                        <Skeleton className="inline-block" loading={true}>
                          +000
                        </Skeleton>
                      </TableCell>
                      <TableCell>
                        <Skeleton className="inline-block" loading={true}>
                          000
                        </Skeleton>
                      </TableCell>
                    </TableRow>
                    <TableRow className="border-zinc-900/10 border-b dark:border-white/20">
                      <TableCell>
                        <Skeleton className="inline-block" loading={true}>
                          DDD
                        </Skeleton>
                      </TableCell>
                      <TableCell>
                        <Skeleton className="inline-block" loading={true}>
                          000 Sport Team +100 at Team
                        </Skeleton>
                      </TableCell>
                      <TableCell>
                        <div className="inline-flex items-center space-x-2">
                          <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-zinc-500/50" />
                          <Skeleton className="inline-block" loading={true}>
                            Win
                          </Skeleton>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Skeleton className="inline-block" loading={true}>
                          00 - 00
                        </Skeleton>
                      </TableCell>
                      <TableCell>
                        <Skeleton className="inline-block" loading={true}>
                          +999
                        </Skeleton>
                      </TableCell>
                      <TableCell>
                        <Skeleton className="inline-block" loading={true}>
                          999
                        </Skeleton>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                ))}
            {rows?.map?.((row) => {
              const result =
                userId === 'archive' ? (row as API.Release) : (row as API.MessageReceipt).release;
              // "Ghost" pick: the member can see this via their subscription but has
              // no receipt for it, so it never counts on their record. Rendered as
              // temporary (dashed accent + clock badge); it drops off after grading.
              const isGhost = userId !== 'archive' && !!(row as API.MessageReceipt).ghost;
              const nextDate = intl.formatDate(
                // @todo both pages should sort by gameTime
                userId === 'archive' ? result.gameTime || result.createdAt : result.createdAt,
                {
                  timeZone: 'America/Chicago', // this needs to be set, or else SSR and CSR will mismatch
                  month: 'long',
                  day: 'numeric',
                  weekday: 'long',
                  year: 'numeric',
                },
              );
              const showDate = lastDate !== nextDate;

              lastDate = nextDate;

              let gameDate = undefined;
              let gameDateSmall = undefined;
              let gameDateFull = '';

              if (result.gameTime) {
                if (isToday(new Date(result.gameTime))) {
                  gameDate = (
                    <>
                      Today
                      <br />
                      {intl.formatDate(result.gameTime, {
                        month: undefined,
                        day: undefined,
                        weekday: undefined,
                        hour: 'numeric',
                        minute: '2-digit',
                        timeZoneName: 'short',
                      })}
                    </>
                  );

                  gameDateSmall = (
                    <>
                      Today
                      <br />
                      {intl.formatDate(result.gameTime, {
                        month: undefined,
                        day: undefined,
                        weekday: undefined,
                        hour: 'numeric',
                        minute: '2-digit',
                        timeZoneName: undefined,
                      })}
                    </>
                  );
                } else {
                  gameDate = (
                    <>
                      {intl.formatDate(result.gameTime, {
                        month: 'short',
                        day: 'numeric',
                        weekday: 'short',
                      })}
                      <br />
                      {intl.formatDate(result.gameTime, {
                        month: undefined,
                        day: undefined,
                        weekday: undefined,
                        hour: 'numeric',
                        minute: '2-digit',
                        timeZoneName: 'short',
                      })}
                    </>
                  );

                  gameDateSmall = (
                    <>
                      {intl.formatDate(result.gameTime, {
                        month: 'short',
                        day: 'numeric',
                        weekday: 'short',
                      })}
                      <br />
                      {intl.formatDate(result.gameTime, {
                        month: undefined,
                        day: undefined,
                        weekday: undefined,
                        hour: 'numeric',
                        minute: '2-digit',
                        timeZoneName: undefined,
                      })}
                    </>
                  );
                }

                gameDateFull = intl.formatDate(result.gameTime, {
                  hour: 'numeric',
                  minute: '2-digit',
                  timeZoneName: 'short',
                });
              }

              return (
                <Fragment key={result.id}>
                  {showDate && (
                    <TableRow className="border-zinc-900/10 border-b dark:border-white/20">
                      <TableHead
                        colSpan={colSpan}
                        scope="colgroup"
                        className="bg-zinc-50 py-2 pr-3 pl-4 text-left font-normal text-sm text-zinc-900 sm:pl-3 dark:bg-white/5 dark:text-white"
                      >
                        {nextDate}
                      </TableHead>
                    </TableRow>
                  )}
                  <TableRow
                    className={twMerge(
                      isGhost &&
                        'border-amber-400 border-l-2 border-dashed bg-amber-50/40 dark:bg-amber-400/5',
                    )}
                  >
                    <TableCell className="whitespace-nowrap text-xs text-zinc-500" breakpoint="md">
                      {result.sport?.shortName}
                    </TableCell>
                    <TableCell>
                      <span>{formatReleaseLine(result, false, true)}</span>
                      {result.gameType === 'fcs' && (
                        <span className="ml-2 rounded-sm bg-blue-400/20 p-1 px-1.5 font-medium text-blue-800 text-xs dark:bg-blue-400/30 dark:text-blue-400">
                          FCS
                        </span>
                      )}
                      {result.gameType === 'extra' && (
                        <span className="ml-2 rounded-sm bg-purple-400/20 p-1 px-1.5 font-medium text-purple-800 text-xs dark:bg-purple-400/30 dark:text-purple-400">
                          Extra
                        </span>
                      )}
                      {!!result.free && (
                        <span className="ml-2 rounded-sm bg-emerald-400/20 p-1 px-1.5 font-medium text-emerald-800 text-xs dark:bg-emerald-400/30 dark:text-emerald-400">
                          Free
                        </span>
                      )}
                      {result.rating !== 1 && (
                        <span className="ml-2 rounded-sm bg-zinc-900/5 p-1 px-1.5 font-medium text-xs dark:bg-white/5">
                          {result.rating} units
                        </span>
                      )}
                      {isGhost && (
                        <ClockIcon
                          title="Released before your subscription — not counted on your record; disappears after grading"
                          aria-label="Released before your subscription — not counted on your record; disappears after grading"
                          className="ml-2 inline-block h-4 w-4 shrink-0 align-middle text-amber-500 dark:text-amber-400"
                        />
                      )}
                      <div className="hidden text-xs text-zinc-500">
                        {result?.sport?.shortName}{' '}
                        {result.playType[0].toUpperCase() + result.playType.substring(1)}
                      </div>
                    </TableCell>
                    {/* Wrap on phones so the result label + score sub-line don't
                      force the table wider than the viewport (sm+ stays nowrap). */}
                    <TableCell className="whitespace-normal sm:whitespace-nowrap">
                      <div className="inline-flex flex-col items-baseline space-x-2">
                        <div className="inline-flex items-center space-x-2">
                          <span
                            className={twMerge(
                              `h-2 w-2 shrink-0 rounded-full bg-zinc-300`,
                              result.result === 'win' && 'bg-emerald-400',
                              result.result === 'loss' && 'bg-rose-600',
                              result.result === 'pending' && 'bg-amber-300',
                            )}
                          />
                          <span>
                            {result.result === 'noAction'
                              ? 'No Action'
                              : result.result[0].toUpperCase() + result.result.substring(1)}
                          </span>
                        </div>
                        <div className="text-xs text-zinc-500 lg:hidden">
                          <span>
                            {formatScore(
                              result.visitor?.shortName,
                              result.home?.shortName,
                              result.visitorScore,
                              result.homeScore,
                              result.overtimes,
                            )}
                          </span>
                          {result.result === 'pending' && gameDateSmall && (
                            <span
                              className="block text-zinc-400 dark:text-zinc-500"
                              title={gameDateFull}
                            >
                              {gameDateSmall}
                            </span>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-zinc-500" breakpoint="lg">
                      <span className="flex flex-row items-center space-x-1">
                        {result.result !== 'pending' ? (
                          <>
                            {/* {!(result.homeScore === null && result.visitorScore === null) && (
                            <ChevronRightIcon
                              className="ml-0.5 mr-1.5 h-4 w-auto shrink-0"
                              aria-hidden="true"
                            />
                          )} */}
                            <span>
                              {formatScore(
                                result.visitor?.shortName,
                                result.home?.shortName,
                                result.visitorScore,
                                result.homeScore,
                                result.overtimes,
                              )}
                            </span>
                          </>
                        ) : (
                          gameDate && (
                            <span className="text-zinc-400 dark:text-zinc-500" title={gameDateFull}>
                              {gameDate}
                            </span>
                          )
                        )}
                      </span>

                      {result?.resultText}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-zinc-500" breakpoint="lg">
                      {result.closingSide === 0 ? (
                        <span>PK</span>
                      ) : result.closingSide ? (
                        <span>
                          {`${
                            result.sideIndicator === 'visitor'
                              ? result.visitor?.shortName
                              : result.sideIndicator === 'home' && result.home?.shortName
                          } ${formatValue(result.closingSide)}`}
                        </span>
                      ) : (
                        <>
                          {result.closingTotal && result.totalIndicator ? (
                            <span>
                              {`${result.totalIndicator[0].toUpperCase()} ${result.closingTotal}`}
                            </span>
                          ) : result.result !== 'pending' ? (
                            <>&mdash;</>
                          ) : (
                            <></>
                          )}
                        </>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-zinc-500">
                      {formatValue(
                        result.closingLineValue,
                        result.result !== 'pending' ? <>&mdash;</> : '',
                        '+0.0',
                      )}
                      {result.closingLineValue && <span className="md:hidden"> CLV</span>}
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="whitespace-nowrap text-emerald-500 text-xs hover:text-emerald-400 dark:text-emerald-500">
                        <UpdateReleaseForm
                          release={result}
                          // Reconcile every results list (pending + graded) on a
                          // grade so the play moves across live; fall back to just
                          // this list when rendered outside the provider.
                          onSuccessfulSubmit={reconcileResults ?? infiniteRequest?.refetch}
                        >
                          <PencilSquareIcon className="size-4 shrink-0" />
                        </UpdateReleaseForm>
                      </TableCell>
                    )}
                  </TableRow>
                </Fragment>
              );
            })}
            {!hasRows && !isPending && (
              <TableRow className="hover:bg-transparent!">
                <TableCell colSpan={colSpan} className="py-8! text-center">
                  No Results
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {!!error?.message && <>Error: {error.message}</>}

      {!hidePagination && (
        <section className="w-full select-none">
          <Pagination>
            <PaginationPrevious
              href={
                menuHref(search, {
                  page: Math.max(page - 1, 1) === 1 ? undefined : Math.max(page - 1, 1),
                }) || pathname
              }
              isDisabled={page <= 1}
            />
            <PaginationList>
              {pageCount > 5 && page >= 4 && (
                <PaginationPage href={menuHref(search, { page: undefined }) || pathname}>
                  1
                </PaginationPage>
              )}
              {pageCount > 6 && page >= 5 && <PaginationGap />}
              {new Array(pageCount < 5 ? pageCount : 5)
                .fill(Math.max(1, Math.min(page - 2, pageCount - 4)))
                .map((start, idx) => {
                  const npage = start + idx;
                  return (
                    <PaginationPage
                      key={npage}
                      href={menuHref(search, { page: npage === 1 ? undefined : npage }) || pathname}
                      current={page === npage}
                      isLoading={isLoading || isPending}
                    >{`${npage}`}</PaginationPage>
                  );
                })}
              {pageCount > 6 && pageCount - page - 2 >= 2 && <PaginationGap />}
              {pageCount > 5 && pageCount - page - 2 >= 1 && (
                <PaginationPage
                  href={
                    menuHref(search, { page: pageCount === 1 ? undefined : pageCount }) || pathname
                  }
                >{`${pageCount}`}</PaginationPage>
              )}
            </PaginationList>
            <PaginationNext
              href={
                menuHref(search, {
                  page:
                    Math.min(page + 1, pageCount) === 1 ? undefined : Math.min(page + 1, pageCount),
                }) || pathname
              }
              isDisabled={page >= pageCount}
            />
          </Pagination>
        </section>
      )}
    </>
  );
}

/**
 * Mobile (below md) rendering of the results/pending list as stacked play-cards,
 * reusing the Today's Releases pending-row card language (#1538) for consistency.
 * Rendered alongside the desktop table (which is `hidden md:block`); both read the
 * same `rows`, so a grade reconciles both live. Kept in this file so the score/CLV
 * formatters and the ghost/badge conventions stay a single source of truth with the
 * table above.
 */
function ResultsCardList<const UID extends API.Id | 'me' | 'archive'>({
  rows,
  userId,
  isAdmin,
  isPending,
  hasRows,
  onGrade,
}: {
  rows: (API.Release | API.MessageReceipt)[];
  userId: UID;
  isAdmin: boolean;
  isPending: boolean;
  hasRows: boolean;
  onGrade?: () => void;
}) {
  const intl = useLocale();
  let lastDate: string | undefined;

  return (
    <ul className="mt-6 flex flex-col gap-2 md:hidden">
      {!hasRows &&
        isPending &&
        Array(4)
          .fill(1)
          .map((_, index) => (
            <li
              key={index}
              className="rounded-xl border border-zinc-950/10 bg-white p-3 shadow-xs dark:border-white/10 dark:bg-zinc-900"
            >
              {/* Mirror the real card: a title line (+ the admin pencil slot), then
                  the status-dot meta line, so the skeleton reads as the same shape. */}
              <div className="flex items-start justify-between gap-2">
                <div className="h-4 w-2/3 animate-pulse rounded-sm bg-zinc-200 dark:bg-zinc-700" />
                {isAdmin && (
                  <div className="size-4 shrink-0 animate-pulse rounded-sm bg-zinc-200 dark:bg-zinc-700" />
                )}
              </div>
              <div className="mt-2 flex items-center gap-1.5">
                <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-zinc-200 dark:bg-zinc-700" />
                <div className="h-3 w-1/3 animate-pulse rounded-sm bg-zinc-200 dark:bg-zinc-700" />
              </div>
            </li>
          ))}

      {rows?.map?.((row) => {
        const result =
          userId === 'archive' ? (row as API.Release) : (row as API.MessageReceipt).release;
        // Ghost pick: visible via subscription but no receipt — never counts on the
        // member's record, drops off after grading. Shown with the amber dashed accent.
        const isGhost = userId !== 'archive' && !!(row as API.MessageReceipt).ghost;

        const nextDate = intl.formatDate(
          userId === 'archive' ? result.gameTime || result.createdAt : result.createdAt,
          {
            timeZone: 'America/Chicago', // set, or SSR and CSR mismatch
            month: 'long',
            day: 'numeric',
            weekday: 'long',
            year: 'numeric',
          },
        );
        const showDate = lastDate !== nextDate;
        lastDate = nextDate;

        const graded = result.result !== 'pending';
        const score = formatScore(
          result.visitor?.shortName,
          result.home?.shortName,
          result.visitorScore,
          result.homeScore,
          result.overtimes,
        );

        // useLocale().formatDate MERGES a default option set (long month, weekday,
        // year), so every field we don't want must be passed as `undefined` or it
        // leaks back in (was double-printing the date). Today drops the date part.
        let gameTimeInline = '';
        let gameTimeFull = '';
        if (result.gameTime) {
          const isTodayGame = isToday(new Date(result.gameTime));
          gameTimeInline = intl.formatDate(result.gameTime, {
            weekday: isTodayGame ? undefined : 'short',
            month: isTodayGame ? undefined : 'short',
            day: isTodayGame ? undefined : 'numeric',
            year: undefined,
            hour: 'numeric',
            minute: '2-digit',
            timeZoneName: undefined,
          });
          if (isTodayGame) {
            gameTimeInline = `Today ${gameTimeInline}`;
          }
          gameTimeFull = intl.formatDate(result.gameTime, {
            weekday: undefined,
            month: undefined,
            day: undefined,
            year: undefined,
            hour: 'numeric',
            minute: '2-digit',
            timeZoneName: 'short',
          });
        }

        const statusLabel =
          result.result === 'noAction'
            ? 'No Action'
            : result.result[0].toUpperCase() + result.result.substring(1);

        return (
          <Fragment key={result.id}>
            {showDate && (
              <li className="mt-2 px-1 font-medium text-sm text-zinc-900 first:mt-0 dark:text-white">
                {nextDate}
              </li>
            )}
            <li
              className={twMerge(
                'rounded-xl border border-zinc-950/10 bg-white p-3 shadow-xs dark:border-white/10 dark:bg-zinc-900',
                isGhost &&
                  'border-l-2 border-amber-400 border-dashed bg-amber-50/40 dark:bg-amber-400/5',
              )}
            >
              {/* Title (full-width, wraps) on the left; units chip + admin pencil in
                  the top-right corner. Badges sit inline with the title but stay on
                  one line (shrink-0) so FCS never splits to "FC/S" (#1574). */}
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 font-medium text-sm text-zinc-900 dark:text-white">
                  <span className="break-words">{formatReleaseLine(result, false, true)}</span>
                  {result.gameType === 'fcs' && (
                    <span className="shrink-0 whitespace-nowrap rounded-sm bg-blue-400/20 px-1.5 py-0.5 font-medium text-blue-800 text-xs dark:bg-blue-400/30 dark:text-blue-400">
                      FCS
                    </span>
                  )}
                  {result.gameType === 'extra' && (
                    <span className="shrink-0 whitespace-nowrap rounded-sm bg-purple-400/20 px-1.5 py-0.5 font-medium text-purple-800 text-xs dark:bg-purple-400/30 dark:text-purple-400">
                      Extra
                    </span>
                  )}
                  {!!result.free && (
                    <span className="shrink-0 whitespace-nowrap rounded-sm bg-emerald-400/20 px-1.5 py-0.5 font-medium text-emerald-800 text-xs dark:bg-emerald-400/30 dark:text-emerald-400">
                      Free
                    </span>
                  )}
                  {result.rating !== 1 && (
                    <span className="shrink-0 whitespace-nowrap rounded-sm bg-zinc-900/5 px-1.5 py-0.5 font-medium text-xs dark:bg-white/5">
                      {result.rating} units
                    </span>
                  )}
                  {isGhost && (
                    <ClockIcon
                      title="Released before your subscription — not counted on your record; disappears after grading"
                      aria-label="Released before your subscription — not counted on your record; disappears after grading"
                      className="h-4 w-4 shrink-0 text-amber-500 dark:text-amber-400"
                    />
                  )}
                </div>
                {/* Admin edit pencil, top-right. (No per-play units chip — the
                    results view has never surfaced per-play units.) */}
                {isAdmin && (
                  <div className="flex shrink-0 items-center">
                    <UpdateReleaseForm release={result} onSuccessfulSubmit={onGrade}>
                      <PencilSquareIcon className="size-4 shrink-0 text-emerald-500 hover:text-emerald-400" />
                    </UpdateReleaseForm>
                  </div>
                )}
              </div>

              {/* Single meta line: status dot + label · sport · score/time · CLV. */}
              <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-zinc-500 dark:text-zinc-400">
                <span className="flex items-center gap-1.5">
                  <span
                    className={twMerge(
                      'h-2 w-2 shrink-0 rounded-full bg-zinc-300',
                      result.result === 'win' && 'bg-emerald-400',
                      result.result === 'loss' && 'bg-rose-600',
                      result.result === 'pending' && 'bg-amber-300',
                    )}
                  />
                  <span>{statusLabel}</span>
                </span>
                {result.sport?.shortName && (
                  <>
                    <span aria-hidden>·</span>
                    <span>{result.sport.shortName}</span>
                  </>
                )}
                {graded && score && (
                  <>
                    <span aria-hidden>·</span>
                    <span>{score}</span>
                  </>
                )}
                {!graded && gameTimeInline && (
                  <>
                    <span aria-hidden>·</span>
                    <span title={gameTimeFull}>{gameTimeInline}</span>
                  </>
                )}
                {!!result.closingLineValue && (
                  <>
                    <span aria-hidden>·</span>
                    <span>CLV {formatValue(result.closingLineValue, '', '+0.0')}</span>
                  </>
                )}
              </div>
            </li>
          </Fragment>
        );
      })}

      {!hasRows && !isPending && (
        <li className="rounded-xl border border-zinc-950/10 border-dashed py-8 text-center text-sm text-zinc-500 dark:border-white/10 dark:text-zinc-400">
          No Results
        </li>
      )}
    </ul>
  );
}
